import { type Access, type EmployeeStatus, Role } from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { popover } from "../directives/popover";
import { app } from "../init";
import { shared } from "../styles";
import "./entity-filters";

export type EmployeeCellInput = {
    firstName: string;
    lastName: string;
    avatar: string;
    status: EmployeeStatus;
    staffNumber?: number | null;
    role: Role;
    access: Access | null;
};

@customElement("ptc-employee-cell")
export class EmployeeCell extends LitElement {
    @property({ attribute: false })
    employee: EmployeeCellInput | null = null;

    @property({ type: Array })
    positionColors: string[] = [];

    @property({ type: Boolean })
    showStaffNumber = false;

    @property({ type: String })
    nameFormat: "firstLast" | "lastFirst" = "firstLast";

    @property({ type: Boolean })
    showResponsibilities = false;

    static styles = [
        shared,
        css`
            :host {
                display: block;
            }

            .responsibilities {
                font-size: 0.85em;
                cursor: default;
            }
        `,
    ];

    private _renderResponsibilities() {
        const emp = this.employee;
        if (!emp || !this.showResponsibilities || emp.role > Role.Manager || !emp.access) {
            return nothing;
        }
        const { role, access } = emp;

        const employeeCount = app.employees.filter((employee) =>
            app.company?.hasAccess({ role, access }, { employee })
        ).length;

        return html`
            <div
                class="responsibilities"
                slot="trailing"
                ${popover(
                    html`
                        <div class="small padded semibold">
                            <ptc-ui-icon name="icon.object.responsibility"></ptc-ui-icon> Zuständigkeitsbereiche
                        </div>
                        <ptc-entity-filters
                            .filters=${role > Role.Owner ? access.filters : []}
                            readonly
                            hideFilterIcon
                            hideEmployeeCount
                        ></ptc-entity-filters>
                    `,
                    {
                        trigger: "hover",
                        class: "non-interactive text-left-aligning tooltip",
                        alignment: "right",
                    }
                )}
            >
                ${employeeCount}
                <ptc-ui-icon name="icon.feature.employees"></ptc-ui-icon>
            </div>
        `;
    }

    render() {
        return html`
            <ptc-ui-employee-cell
                .employee=${this.employee}
                .positionColors=${this.positionColors}
                .showStaffNumber=${this.showStaffNumber}
                .nameFormat=${this.nameFormat}
            >
                ${this._renderResponsibilities()}
                <slot name="trailing" slot="trailing"></slot>
            </ptc-ui-employee-cell>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-employee-cell": EmployeeCell;
    }
}
